import { useCallback, useEffect, useRef } from 'react';

import { usePsfSharePageState } from '#src/app/pages/psf/share/pageContext';
import { SHARE_GOAL_COUNTER_CELEBRATION_MS, SHARE_GOAL_COUNTER_STEPS } from '#src/app/pages/psf/share/shared/consts';
import { subscribeToSupporterPresence } from '#src/app/pages/psf/share/shared/utils';
import type { ShareActionsOptions, ShareChannel } from '#src/app/shared/hooks/share';

import { useShareGoalCounter } from '../../useShareGoalCounter';

import { useCreateNewShare } from './useCreateNewShare';
import { useRedirect } from './useRedirect';
import { useTrackPromotionShare } from './useTrackPromotionShare';

export function useOnShareSuccess(): NonNullable<ShareActionsOptions['onSuccess']> {
	const [, { toggleShared, incrementShareCount }] = usePsfSharePageState();
	const { isActive: isCounterActive, count } = useShareGoalCounter();
	const cancelCelebrationRef = useRef<(() => void) | null>(null);
	// Counted synchronously so two shares resolving close together can't both read the same `count`
	// and miss the one that reaches the goal.
	const countRef = useRef(count);

	const trackPromotionShare = useTrackPromotionShare();
	const createNewShare = useCreateNewShare();
	const redirect = useRedirect();

	useEffect(() => () => cancelCelebrationRef.current?.(), []);

	const redirectAfterCelebration = useCallback(
		(channel: ShareChannel) => {
			cancelCelebrationRef.current?.();
			let timer: ReturnType<typeof setTimeout> | undefined;
			/**
			 * The celebration only counts while the supporter is on the page, by the same measure as the counter's
			 * reveal: the timer waits for them and restarts if they leave before it ends (e.g. an app handoff that
			 * opens after the share resolved).
			 */
			const onPresenceChange = (isPresent: boolean) => {
				clearTimeout(timer);
				if (!isPresent) return;
				timer = setTimeout(() => {
					cancel();
					redirect(channel, { redirectOnCopy: true });
				}, SHARE_GOAL_COUNTER_CELEBRATION_MS);
			};
			const unsubscribe = subscribeToSupporterPresence(onPresenceChange);
			const cancel = () => {
				unsubscribe();
				window.removeEventListener('pagehide', cancel);
				clearTimeout(timer);
			};
			cancelCelebrationRef.current = cancel;
			// Leaving the page ends the celebration, so a back/forward cache restore doesn't redirect on its own.
			window.addEventListener('pagehide', cancel);
		},
		[redirect],
	);

	return useCallback(
		async (channel) => {
			// awaiting to make sure it's done before navigating
			await Promise.all([trackPromotionShare(), createNewShare(channel)]);

			if (isCounterActive) {
				incrementShareCount();
				countRef.current += 1;
				// `>=`: a page restored from the back/forward cache after the redirect keeps its count, so its next share
				// lands past the goal.
				if (countRef.current >= SHARE_GOAL_COUNTER_STEPS) redirectAfterCelebration(channel);
			} else {
				redirect(channel);
			}

			toggleShared(true);
		},
		[
			toggleShared,
			incrementShareCount,
			isCounterActive,
			trackPromotionShare,
			createNewShare,
			redirect,
			redirectAfterCelebration,
		],
	);
}
